import { useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { ProcessTimeline, CtaSection } from "../components/Sections";
import { SERVICE_DETAILS } from "../lib/data";
import { BRAND, SITE_URL } from "../lib/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const detail = SERVICE_DETAILS[slug];

  const schema = useMemo(() => {
    if (!detail) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbSchema([["Home", "/"], ["Services", "/services"], [detail.title, `/services/${slug}`]]),
        {
          "@type": "Service",
          name: detail.title,
          serviceType: detail.title,
          description: detail.intro,
          url: `${SITE_URL}/services/${slug}`,
          areaServed: "IN",
          provider: { "@type": "Organization", name: BRAND.name, url: SITE_URL },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${detail.title} specialisations`,
            itemListElement: detail.items.map((item) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: item },
            })),
          },
        },
      ],
    };
  }, [detail, slug]);

  if (!detail) return <Navigate to="/services" replace />;

  return (
    <>
      <Seo title={detail.title} description={detail.intro} keywords={detail.keywords} schema={schema} />
      <PageHeader kicker={detail.kicker} title={detail.title} sub={detail.intro} />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="service-detail">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <img
              src={detail.image}
              alt={detail.imageAlt || detail.title}
              width="940"
              height="650"
              loading="lazy"
              className="w-full h-[380px] object-cover rounded-xl border border-slate-200"
            />
          </Reveal>
          <div>
            <Reveal>
              <h2 className="text-2xl md:text-3xl font-bold text-navy">What we cover</h2>
              <p className="mt-3 text-ink leading-relaxed">Roles and specialisations we recruit for within {detail.title.toLowerCase()}.</p>
            </Reveal>
            <ul className="mt-8 grid sm:grid-cols-2 gap-4">
              {detail.items.map((item, i) => (
                <Reveal key={item} delay={i * 0.05}>
                  <li className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 bg-white">
                    <span className="w-7 h-7 rounded-full bg-sapphire/10 text-sapphire flex items-center justify-center shrink-0" aria-hidden="true"><Check size={16} /></span>
                    <span className="font-medium text-navy">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.2}>
              <div className="mt-8 flex gap-3">
                <Link to="/employers" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sapphire hover:bg-sapphire-dark text-white font-semibold transition-colors" data-testid="service-hire-btn">
                  Hire for this <ArrowUpRight size={18} aria-hidden="true" />
                </Link>
                <Link to="/services" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-navy font-semibold hover:border-navy transition-colors">
                  All Services
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <ProcessTimeline />
      <CtaSection />
    </>
  );
}
